import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheckCircle, FaRocket, FaLock } from "react-icons/fa";
import { getFormApiUrl } from "../../../utils/formApiUrl";

const PROGRAM_OPTIONS = ["CompTIA", "Cisco Networking Academy", "EC-Council"];

const initialFormState = {
  firstName: "",
  secondName: "",
  lastName: "",
  sex: "",
  dateOfBirth: "",
  nationality: "",
  countryOfBirth: "",
  correspondentAddress: "",
  phone: "",
  email: "",
  qualificationTitle: "",
  schoolInstitute: "",
  stateCountry: "",
  yearCompleted: "",
};

const FIELD_LABELS = {
  firstName: "First Name",
  secondName: "Second Name",
  lastName: "Last Name",
  sex: "Sex",
  dateOfBirth: "Date of Birth",
  nationality: "Nationality",
  countryOfBirth: "Country of Birth",
  correspondentAddress: "Correspondent Address",
  phone: "Phone No",
  email: "E-mail",
  qualificationTitle: "Qualification Title",
  schoolInstitute: "School / Institute",
  stateCountry: "State / Country",
  yearCompleted: "Year Completed",
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const ProfessionalCourseApplicationForm = ({ programName = "", fieldLabel = "Program" }) => {
  const isLocked = Boolean(programName);

  const [formData, setFormData] = useState(initialFormState);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const effectiveProgram = isLocked ? programName : selectedProgram;

  const inputCls = (field) =>
    `w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-custom-pink/20 transition-all duration-300 ${
      formErrors[field]
        ? "border-red-400 focus:border-red-400"
        : "border-gray-200 focus:border-custom-pink"
    }`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
    if (submitError) setSubmitError("");
  };

  const validate = () => {
    const errors = {};
    Object.keys(initialFormState).forEach((field) => {
      if (!String(formData[field] || "").trim()) {
        errors[field] = `${FIELD_LABELS[field]} is required`;
      }
    });
    if (!isLocked && !selectedProgram) {
      errors.program = "Program is required";
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildEmailBody = () => {
    const row = (label, value) =>
      `<div class="field"><span class="label">${escapeHtml(label)}:</span><span class="value">${escapeHtml(value || "Not provided")}</span></div>`;
    const submittedAt = new Date().toLocaleString("en-US", {
      dateStyle: "full",
      timeStyle: "long",
      timeZone: "Africa/Dar_es_Salaam",
    });
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background: #f4f4f4; }
          .container { max-width: 650px; margin: 0 auto; background: white; }
          .header { background: linear-gradient(135deg, #9333ea 0%, #3b82f6 100%); color: white; padding: 40px 30px; text-align: center; }
          .header h1 { margin: 0; font-size: 26px; }
          .header p { margin: 10px 0 0 0; opacity: 0.9; }
          .content { padding: 30px; }
          .section { margin-bottom: 25px; padding: 20px; background: #f9fafb; }
          .section-title { font-weight: bold; color: #9333ea; margin-bottom: 15px; font-size: 17px; text-transform: uppercase; letter-spacing: 0.5px; }
          .field { margin-bottom: 10px; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
          .field:last-child { border-bottom: none; }
          .label { font-weight: 600; color: #4b5563; display: inline-block; min-width: 200px; }
          .value { color: #1f2937; word-wrap: break-word; }
          .footer { background: #1f2937; color: white; padding: 25px; text-align: center; font-size: 13px; }
          .footer p { margin: 5px 0; }
          .badge { display: inline-block; background: #9333ea; color: white; padding: 6px 12px; font-size: 11px; font-weight: bold; margin-left: 14px; letter-spacing: 0.03em; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>New Professional Course Application</h1>
            <p>${escapeHtml(effectiveProgram)} &mdash; JRIIT College</p>
          </div>
          <div class="content">
            <div class="section">
              <div class="section-title">Personal Details</div>
              ${row("First Name", formData.firstName)}
              ${row("Second Name", formData.secondName)}
              ${row("Last Name", formData.lastName)}
              ${row("Sex", formData.sex)}
              ${row("Date of Birth", formData.dateOfBirth)}
              ${row("Nationality", formData.nationality)}
              ${row("Country of Birth", formData.countryOfBirth)}
              ${row("Correspondent Address", formData.correspondentAddress)}
              ${row("Phone No", formData.phone)}
              ${row("E-mail", formData.email)}
            </div>
            <div class="section">
              <div class="section-title">Application Details</div>
              <div class="field"><span class="label">Program:</span><span class="value">${escapeHtml(effectiveProgram)}<span class="badge">PROFESSIONAL COURSE</span></span></div>
            </div>
            <div class="section">
              <div class="section-title">Qualification / Academic Records &mdash; Secondary School Studies</div>
              ${row("Qualification Title", formData.qualificationTitle)}
              ${row("School / Institute", formData.schoolInstitute)}
              ${row("State / Country", formData.stateCountry)}
              ${row("Year Completed", formData.yearCompleted)}
            </div>
          </div>
          <div class="footer">
            <p><strong>JRIIT College Admissions Office</strong></p>
            <p>Sakina Area, Arusha Tanzania | P.O. Box 7071 Namanga Road</p>
            <p>+255 754 360 590 | admissions@jriit.ac.tz</p>
            <p style="margin-top: 15px; opacity: 0.8;">Submitted on: ${escapeHtml(submittedAt)}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const url = getFormApiUrl("send-acceptance");
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `New Professional Course Application — ${effectiveProgram}`,
          body: buildEmailBody(),
          replyTo: formData.email,
          replyToName: `${formData.firstName} ${formData.lastName}`,
        }),
      });
      const rawText = await response.text();
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${rawText.slice(0, 200)}`);
      }
      let result;
      try {
        result = JSON.parse(rawText || "{}");
      } catch {
        throw new Error("Server returned a non-JSON response.");
      }
      if (result.success) {
        setSubmitSuccess(true);
      } else {
        throw new Error(result.error || "Submission failed");
      }
    } catch (error) {
      console.error("Professional Course application submission error:", error);
      setSubmitError("Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const dismissSuccess = () => {
    setSubmitSuccess(false);
    setFormData(initialFormState);
    setSelectedProgram("");
    setFormErrors({});
  };

  return (
    <section
      id="application-form"
      className="relative py-16 md:py-20 bg-white overflow-hidden"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-custom-pink leading-tight">
            Application Form
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto rounded-full mt-4" />
          <p className="mt-4 text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
            Fill in your details below to apply for a JRIIT Professional Course. Our team will contact you shortly after submission.
          </p>
        </div>

        <div className="bg-white rounded-3xl p-6 sm:p-8 md:p-12 border border-gray-200 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-10" noValidate>

            {/* Personal Details */}
            <div className="space-y-6">
              <h3 className="text-xl md:text-2xl font-bold text-custom-pink">Personal Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">First Name <span className="text-red-500">*</span></label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className={inputCls("firstName")} placeholder="Enter first name" required />
                  {formErrors.firstName && <p className="text-red-500 text-sm">{formErrors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Second Name <span className="text-red-500">*</span></label>
                  <input type="text" name="secondName" value={formData.secondName} onChange={handleChange} className={inputCls("secondName")} placeholder="Enter second name" required />
                  {formErrors.secondName && <p className="text-red-500 text-sm">{formErrors.secondName}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={inputCls("lastName")} placeholder="Enter last name" required />
                  {formErrors.lastName && <p className="text-red-500 text-sm">{formErrors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Sex <span className="text-red-500">*</span></label>
                  <select name="sex" value={formData.sex} onChange={handleChange} className={inputCls("sex")} required>
                    <option value="" disabled>Select sex</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  {formErrors.sex && <p className="text-red-500 text-sm">{formErrors.sex}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Date of Birth <span className="text-red-500">*</span></label>
                  <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className={inputCls("dateOfBirth")} required />
                  {formErrors.dateOfBirth && <p className="text-red-500 text-sm">{formErrors.dateOfBirth}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Nationality <span className="text-red-500">*</span></label>
                  <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} className={inputCls("nationality")} placeholder="e.g. Tanzanian" required />
                  {formErrors.nationality && <p className="text-red-500 text-sm">{formErrors.nationality}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Country of Birth <span className="text-red-500">*</span></label>
                  <input type="text" name="countryOfBirth" value={formData.countryOfBirth} onChange={handleChange} className={inputCls("countryOfBirth")} placeholder="e.g. Tanzania" required />
                  {formErrors.countryOfBirth && <p className="text-red-500 text-sm">{formErrors.countryOfBirth}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-gray-700 font-medium">Correspondent Address <span className="text-red-500">*</span></label>
                <textarea name="correspondentAddress" value={formData.correspondentAddress} onChange={handleChange} rows={3} className={`${inputCls("correspondentAddress")} resize-none`} placeholder="e.g. P.O. Box 1234, Arusha, Tanzania" required />
                {formErrors.correspondentAddress && <p className="text-red-500 text-sm">{formErrors.correspondentAddress}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">Phone No <span className="text-red-500">*</span></label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputCls("phone")} placeholder="e.g. +255 754 000 000" required />
                  {formErrors.phone && <p className="text-red-500 text-sm">{formErrors.phone}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-gray-700 font-medium">E-mail <span className="text-red-500">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputCls("email")} placeholder="you@example.com" required />
                  {formErrors.email && <p className="text-red-500 text-sm">{formErrors.email}</p>}
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="space-y-6 pt-2 border-t border-gray-100">
              <h3 className="text-xl md:text-2xl font-bold text-custom-pink pt-6">Application Details</h3>

              <div className="space-y-2">
                {isLocked ? (
                  <>
                    <label className="text-gray-700 font-medium flex items-center gap-2">
                      {fieldLabel}
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-custom-pink bg-custom-pink/10 px-2 py-1 rounded-md">
                        <FaLock className="text-[10px]" /> Locked
                      </span>
                    </label>
                    <input
                      type="text"
                      value={programName}
                      readOnly
                      aria-readonly="true"
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-700 font-semibold cursor-not-allowed"
                    />
                  </>
                ) : (
                  <>
                    <label className="text-gray-700 font-medium">{fieldLabel} <span className="text-red-500">*</span></label>
                    <select
                      value={selectedProgram}
                      onChange={(e) => {
                        setSelectedProgram(e.target.value);
                        if (formErrors.program) setFormErrors((prev) => ({ ...prev, program: "" }));
                        if (submitError) setSubmitError("");
                      }}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-800 focus:ring-2 focus:ring-custom-pink/20 transition-all duration-300 ${formErrors.program ? "border-red-400 focus:border-red-400" : "border-gray-200 focus:border-custom-pink"}`}
                      required
                    >
                      <option value="" disabled>Select a program</option>
                      {PROGRAM_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {formErrors.program && <p className="text-red-500 text-sm">{formErrors.program}</p>}
                  </>
                )}
              </div>
            </div>

            {/* Qualification / Academic Records */}
            <div className="space-y-6 pt-2 border-t border-gray-100">
              <h3 className="text-xl md:text-2xl font-bold text-custom-pink pt-6">Qualification / Academic Records</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Please provide details of all secondary and tertiary studies completed or awaiting results, including an explanation of the grading system used.
              </p>

              <div className="space-y-6 pt-2">
                <h4 className="text-lg font-semibold text-gray-900">Secondary School Studies</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium">Qualification Title <span className="text-red-500">*</span></label>
                    <input type="text" name="qualificationTitle" value={formData.qualificationTitle} onChange={handleChange} className={inputCls("qualificationTitle")} placeholder="e.g. Certificate of Secondary Education" required />
                    {formErrors.qualificationTitle && <p className="text-red-500 text-sm">{formErrors.qualificationTitle}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium">School / Institute <span className="text-red-500">*</span></label>
                    <input type="text" name="schoolInstitute" value={formData.schoolInstitute} onChange={handleChange} className={inputCls("schoolInstitute")} placeholder="Name of school or institute" required />
                    {formErrors.schoolInstitute && <p className="text-red-500 text-sm">{formErrors.schoolInstitute}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium">State / Country <span className="text-red-500">*</span></label>
                    <input type="text" name="stateCountry" value={formData.stateCountry} onChange={handleChange} className={inputCls("stateCountry")} placeholder="e.g. Arusha, Tanzania" required />
                    {formErrors.stateCountry && <p className="text-red-500 text-sm">{formErrors.stateCountry}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium">Year Completed <span className="text-red-500">*</span></label>
                    <input type="text" name="yearCompleted" value={formData.yearCompleted} onChange={handleChange} className={inputCls("yearCompleted")} placeholder="e.g. 2023" required />
                    {formErrors.yearCompleted && <p className="text-red-500 text-sm">{formErrors.yearCompleted}</p>}
                  </div>
                </div>
              </div>
            </div>

            {submitError && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`inline-flex items-center gap-2 px-8 py-3 rounded-xl transition-all duration-300 font-medium text-white shadow-md ${
                  isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-custom-pink hover:bg-purple-800"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>Submit Application <FaRocket className="ml-1" /></>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="prof-course-success-title"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={dismissSuccess}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-6xl text-green-500" />
              </div>
              <h3 id="prof-course-success-title" className="text-2xl font-bold text-gray-900 mb-4">
                Application Submitted!
              </h3>
              <p className="text-gray-600 mb-6">
                Your application has been submitted successfully. We will contact you shortly.
              </p>
              <button
                type="button"
                className="bg-custom-pink text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-800 transition-colors"
                onClick={dismissSuccess}
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProfessionalCourseApplicationForm;
